import { useState } from 'react';
import type {
  CreateFormFieldDto,
  FormFieldType,
  FormFieldValueDto,
  FormFieldWithValues,
} from '../services/hubClient';

const FIELD_TYPES: FormFieldType[] = ['input', 'select', 'file', 'confirm'];

interface Props {
  /** Existing field to edit, or null to create a new one. */
  field: FormFieldWithValues | null;
  /** Default ordering for a new field (appended at the end). */
  defaultOrdering: number;
  /** Other fields in this form — selectable as "hide when chosen" targets. */
  otherFields: { id: string; name: string }[];
  saving: boolean;
  onSave: (dto: CreateFormFieldDto) => void;
  onClose: () => void;
}

interface OptionRow extends FormFieldValueDto {
  hide: string[];
}

const labelCls = 'block text-xs font-medium text-gray-500 mb-1';
const inputCls = 'w-full rounded border border-gray-300 px-3 py-2 text-sm';

export function FieldEditor({
  field,
  defaultOrdering,
  otherFields,
  saving,
  onSave,
  onClose,
}: Props) {
  const [name, setName] = useState(field?.name ?? '');
  const [question, setQuestion] = useState(field?.question ?? '');
  const [type, setType] = useState<FormFieldType>(field?.type ?? 'input');
  const [isRequired, setIsRequired] = useState(field?.isRequired ?? true);
  const [regex, setRegex] = useState(field?.regex ?? '');
  const [invalidReply, setInvalidReply] = useState(field?.invalidReply ?? '');
  const [ordering, setOrdering] = useState(field?.ordering ?? defaultOrdering);
  const [maxRetries, setMaxRetries] = useState<string>(
    field?.maxRetries != null ? String(field.maxRetries) : '',
  );
  const [tag, setTag] = useState(field?.tag ?? '');
  const [options, setOptions] = useState<OptionRow[]>(
    (field?.values ?? []).map((v) => ({
      label: v.label,
      value: v.value,
      ordering: v.ordering,
      hide: v.hide ?? [],
    })),
  );

  const isSelect = type === 'select';
  const nameError = !name.trim() ? 'Slot key is required' : '';
  const questionError = !question.trim() ? 'Question is required' : '';
  const optionsError =
    isSelect && options.length === 0 ? 'A select field needs at least one option' : '';
  const canSave = !nameError && !questionError && !optionsError && !saving;

  function addOption() {
    setOptions((prev) => [...prev, { label: '', value: '', ordering: prev.length, hide: [] }]);
  }
  function updateOption(i: number, patch: Partial<OptionRow>) {
    setOptions((prev) => prev.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));
  }
  function removeOption(i: number) {
    setOptions((prev) => prev.filter((_, idx) => idx !== i));
  }
  function toggleHide(i: number, fieldId: string) {
    setOptions((prev) =>
      prev.map((o, idx) => {
        if (idx !== i) return o;
        const has = o.hide.includes(fieldId);
        return { ...o, hide: has ? o.hide.filter((h) => h !== fieldId) : [...o.hide, fieldId] };
      }),
    );
  }

  function submit() {
    const dto: CreateFormFieldDto = {
      name: name.trim(),
      question: question.trim(),
      type,
      isRequired,
      ordering,
    };
    if (regex.trim()) dto.regex = regex.trim();
    if (invalidReply.trim()) dto.invalidReply = invalidReply.trim();
    if (tag.trim()) dto.tag = tag.trim();
    if (maxRetries.trim() && !Number.isNaN(Number(maxRetries))) {
      dto.maxRetries = Number(maxRetries);
    }
    if (isSelect) {
      dto.values = options.map((o, i) => ({
        label: o.label.trim(),
        value: o.value.trim(),
        ordering: i,
        hide: o.hide,
      }));
    }
    onSave(dto);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <h3 className="text-sm font-semibold">{field ? 'Edit field' : 'New field'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Slot key (name)</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputCls}
                placeholder="e.g. phone_number"
              />
              {nameError && <p className="mt-1 text-xs text-red-600">{nameError}</p>}
            </div>
            <div>
              <label className={labelCls}>Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as FormFieldType)}
                className={inputCls}
              >
                {FIELD_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className={labelCls}>Question (shown to user)</label>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              className={inputCls}
              rows={2}
              placeholder="What is your phone number?"
            />
            {questionError && <p className="mt-1 text-xs text-red-600">{questionError}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Validation regex (optional)</label>
              <input
                value={regex}
                onChange={(e) => setRegex(e.target.value)}
                className={inputCls}
                placeholder="^\\+?\\d{8,}$"
              />
            </div>
            <div>
              <label className={labelCls}>Invalid reply (optional)</label>
              <input
                value={invalidReply}
                onChange={(e) => setInvalidReply(e.target.value)}
                className={inputCls}
                placeholder="That doesn't look right, try again."
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Ordering</label>
              <input
                type="number"
                value={ordering}
                onChange={(e) => setOrdering(Number(e.target.value))}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Max retries (optional)</label>
              <input
                type="number"
                value={maxRetries}
                onChange={(e) => setMaxRetries(e.target.value)}
                className={inputCls}
                placeholder="default"
              />
            </div>
            <div>
              <label className={labelCls}>Tag (optional)</label>
              <input
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                className={inputCls}
                placeholder="email | phone"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isRequired}
              onChange={(e) => setIsRequired(e.target.checked)}
            />
            Required
          </label>

          {isSelect && (
            <div className="rounded border border-gray-200 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-700">Options</span>
                <button
                  onClick={addOption}
                  className="rounded bg-gray-100 px-2 py-1 text-xs hover:bg-gray-200"
                >
                  + Add option
                </button>
              </div>
              {optionsError && <p className="mb-2 text-xs text-red-600">{optionsError}</p>}
              <div className="space-y-3">
                {options.map((o, i) => (
                  <div key={i} className="rounded border border-gray-100 bg-gray-50 p-2">
                    <div className="flex items-center gap-2">
                      <input
                        value={o.label}
                        onChange={(e) => updateOption(i, { label: e.target.value })}
                        className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                        placeholder="Label (shown)"
                      />
                      <input
                        value={o.value}
                        onChange={(e) => updateOption(i, { value: e.target.value })}
                        className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                        placeholder="Value (stored)"
                      />
                      <button
                        onClick={() => removeOption(i)}
                        className="px-2 text-gray-400 hover:text-red-600"
                        title="Remove option"
                      >
                        ✕
                      </button>
                    </div>
                    {otherFields.length > 0 && (
                      <div className="mt-2">
                        <span className="text-[11px] text-gray-400">
                          Hide these fields if chosen:
                        </span>
                        <div className="mt-1 flex flex-wrap gap-2">
                          {otherFields.map((f) => (
                            <label
                              key={f.id}
                              className="flex items-center gap-1 text-[11px] text-gray-600"
                            >
                              <input
                                type="checkbox"
                                checked={o.hide.includes(f.id)}
                                onChange={() => toggleHide(i, f.id)}
                              />
                              {f.name}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {options.length === 0 && (
                  <p className="text-xs text-gray-400">No options yet.</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button onClick={onClose} className="rounded px-3 py-1.5 text-sm text-gray-600">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!canSave}
            className="rounded bg-blue-600 px-4 py-1.5 text-sm text-white disabled:opacity-40"
          >
            {saving ? 'Saving…' : 'Save field'}
          </button>
        </div>
      </div>
    </div>
  );
}
